import "@src/utils/faker/extensions";
import "@src/utils/faker/seed";

import "@el8/vital/dist/vital.css";
import "@src/utils/http/initApiClient";
import "@src/utils/msw/initMSW";
import "@src/utils/observability/sentry";
import "@src/utils/observability/datadog";
import { StrictMode, useMemo } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import "./app.css";
import { useStitchContainer } from "./hooks/useStitchContainer";
import { useOktaAuthInitialization } from "./okta/auth";
import { IsChromeExtensionProvider } from "./providers/isChromeExtension";
import { ReactQueryClientProvider } from "./providers/reactQuery";
import { UrlContextProvider } from "./providers/urlContext";
import { queryClient } from "./queryClient";
import Account from "./routes/account/index";
import Billing from "./routes/billing/index";
import CreateAccount, {
  clientAction as createAccountClientAction,
} from "./routes/create-account/index";
import DashboardNotFound from "./routes/dashboard-not-found/index";
import Dashboard, {
  clientLoader as dashboardLoader,
} from "./routes/dashboard/index";
import { ErrorBoundary } from "./routes/error-boundary";
import Home, { clientLoader as homeLoader } from "./routes/home/index";
import AuthenticatedLayout from "./routes/layouts/authenticated/index";
import DashboardLayout, {
  clientLoader as dashboardLayoutLoader,
} from "./routes/layouts/dashboard/index";
import PatientLayout from "./routes/layouts/patient/index";
import LoginCallback from "./routes/login/callback/index";
import Login from "./routes/login/index";
import Logout, { clientAction as logoutAction } from "./routes/logout/index";
import PatientBriefingRoute from "./routes/patient-briefing/index";
import PatientEncounterRoute from "./routes/patient-encounter/index";
import PatientEncountersRoute from "./routes/patient-encounters/index";
import PatientIndex from "./routes/patient-index/index";
import PatientIndexRedirect from "./routes/patient-index/redirect";
import PatientTodayRoute from "./routes/patient-today/index";
import Patients from "./routes/patients/index";
import Root from "./routes/root";

function createRouter() {
  const router = createBrowserRouter([
    {
      id: "root",
      path: "/",
      element: <Root />,
      errorElement: <ErrorBoundary />,
      children: [
        {
          index: true,
          element: <Home />,
          loader: homeLoader,
        },
        {
          path: "login",
          element: <Login />,
        },
        {
          path: "login/callback",
          element: <LoginCallback />,
        },
        {
          path: "create-account",
          element: <CreateAccount />,
          action: createAccountClientAction,
        },
        {
          id: "dashboard",
          path: "/dashboard",
          element: <AuthenticatedLayout />,
          children: [
            {
              path: "",
              element: <DashboardLayout />,
              loader: dashboardLayoutLoader,
              children: [
                {
                  index: true,
                  element: <Dashboard />,
                  loader: dashboardLoader,
                },
                {
                  path: "account",
                  element: <Account />,
                },
                {
                  path: "not-found",
                  element: <DashboardNotFound />,
                },
                {
                  path: "patients",
                  element: <Patients />,
                },
                {
                  path: "patients/:patientId",
                  element: <PatientLayout />,
                  children: [
                    {
                      element: <PatientIndex />,
                      children: [
                        {
                          index: true,
                          element: <PatientIndexRedirect />,
                        },
                        {
                          path: "briefing",
                          element: <PatientBriefingRoute />,
                        },
                        {
                          path: "today",
                          element: <PatientTodayRoute />,
                        },
                        {
                          path: "encounters",
                          element: <PatientEncountersRoute />,
                        },
                        {
                          path: "encounter/:encounterId",
                          element: <PatientEncounterRoute />,
                        },
                      ],
                    },
                  ],
                },
                {
                  path: "billing",
                  element: <Billing />,
                },
                {
                  path: "logout",
                  element: <Logout />,
                  action: logoutAction,
                },
              ],
            },
          ],
        },
      ],
    },
  ]);

  return router;
}

const root = document.getElementById("root");

/**
 * Web Application (Data Mode)
 *
 * Uses createBrowserRouter for client-side routing with data loading.
 * All routes are defined programmatically here, similar to the Chrome extension.
 * The Root component wraps all routes with Okta Security inside the router context.
 */
function App() {
  useStitchContainer();

  const oktaAuth = useOktaAuthInitialization();

  const router = useMemo(() => {
    if (oktaAuth) return createRouter();
    return null;
  }, [oktaAuth]);

  if (!oktaAuth) {
    return <></>;
  }

  if (!router) {
    return <></>;
  }

  return (
    <StrictMode>
      <div id="stitch-container">
        <IsChromeExtensionProvider isChromeExtension={false}>
          <ReactQueryClientProvider queryClient={queryClient}>
            <UrlContextProvider>
              <RouterProvider router={router} />
            </UrlContextProvider>
          </ReactQueryClientProvider>
        </IsChromeExtensionProvider>
      </div>
    </StrictMode>
  );
}

if (root) {
  createRoot(root).render(<App />);
}
